import type { RefObject } from 'react';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { OptionRow } from './option-row';
import type { OptionRowHandlers } from './option-row';
import { useOptionVirtualizer } from '../hooks';

const PRODUCT_HEADER_HEIGHT = 130;
const PRODUCT_HEADER_HEIGHT_WITH_NOTE = 146;

interface ProductOptionsProps {
  product: QuoteProductDTO;
  isSectionChild?: boolean;
  searchQuery?: string;
  onProductClick?: (product: QuoteProductDTO) => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  productStartOffset?: number;
  isQuoteEditable: boolean;
  optionHandlers: OptionRowHandlers;
  isRfqQuote?: boolean;
}

export const ProductOptions = ({
  product,
  isSectionChild,
  searchQuery,
  onProductClick,
  scrollContainerRef,
  productStartOffset,
  isQuoteEditable,
  optionHandlers,
  isRfqQuote
}: ProductOptionsProps) => {
  const children = product?.children;
  const hasChildren = !!children && children.length > 0;

  const shouldVirtualize =
    hasChildren && !!scrollContainerRef && productStartOffset !== undefined;

  const emptyRef = { current: null } as RefObject<HTMLDivElement>;

  const { virtualItems, totalSize } = useOptionVirtualizer({
    options: children || [],
    scrollContainerRef: scrollContainerRef || emptyRef,
    productStartOffset: productStartOffset ?? 0,
    productHeaderHeight: product?.note?.trim()
      ? PRODUCT_HEADER_HEIGHT_WITH_NOTE
      : PRODUCT_HEADER_HEIGHT,
    enabled: shouldVirtualize
  });

  if (!hasChildren) return null;

  return (
    <div
      style={{
        position: shouldVirtualize ? 'relative' : undefined,
        height: shouldVirtualize ? totalSize : undefined
      }}>
      {shouldVirtualize
        ? virtualItems.map(virtualOption => {
            const childProduct = children[virtualOption.index];
            return (
              <div
                key={childProduct.id || childProduct._id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualOption.start}px)`
                }}>
                <OptionRow
                  isSectionChild={isSectionChild}
                  product={childProduct}
                  searchQuery={searchQuery}
                  onProductClick={onProductClick}
                  isLastChild={virtualOption.index === children.length - 1}
                  isQuoteEditable={isQuoteEditable}
                  handlers={optionHandlers}
                  isRfqQuote={isRfqQuote}
                />
              </div>
            );
          })
        : children.map((childProduct, index) => (
            <OptionRow
              isSectionChild={isSectionChild}
              key={childProduct.id || childProduct._id}
              product={childProduct}
              searchQuery={searchQuery}
              onProductClick={onProductClick}
              isLastChild={index === children.length - 1}
              isQuoteEditable={isQuoteEditable}
              handlers={optionHandlers}
            />
          ))}
    </div>
  );
};
